import type { SchedulingCommandDraft } from './scheduling-command-stub-parser';
import { addMinutesISO } from '@/scheduling/lib/quick-create-slot';

/** What a successful save hands back (mirror of the panel's SavedBookingSeed —
 *  kept structural so this module stays UI-free). */
export interface PromoteSavedSeed {
  patientId?: string;
  patientName?: string;
  slotStart?: string;
  slotEnd?: string;
}

/**
 * Promote the next back-to-back chain leg after a successful save — the
 * PURE half of the frame's advanceAiChain (extracted for audit test 3;
 * the frame keeps only the setState choreography around it).
 *
 * Contract (audit #4, Codex-reviewed):
 *  - The next leg starts when the booked visit ENDS: real slotEnd first,
 *    ghost-math fallback (prior start + prior duration, default 30).
 *  - The HUMAN-confirmed patient rides the promotion (patientId is never
 *    a resolution — assurance-model rule, SB/1729560577).
 *  - The booked visit joins chainDone as a history label; the promoted
 *    leg leaves `chain`. chainDone is the SINGLE source of chain progress.
 *  - Returns null when there is nothing to promote.
 */
export function promoteChainLeg(
  current: SchedulingCommandDraft,
  saved?: PromoteSavedSeed,
): SchedulingCommandDraft | null {
  if (!current.chain || current.chain.length === 0) return null;
  const [nextLeg, ...rest] = current.chain;

  const bookedLabel = `${current.profileQuery ?? 'Visit'}${
    current.doctorQuery ? ` with ${current.doctorQuery}` : ''
  } — booked`;

  const nextStart =
    saved?.slotEnd ??
    (current.start
      ? addMinutesISO(current.start, current.durationMinutes ?? 30)
      : undefined);

  return {
    // Keep quoting the ORIGINAL utterance in the rail's RequestHeader —
    // the leg is still part of the same spoken request.
    ...(current.raw ? { raw: current.raw } : {}),
    ...(current.source ? { source: current.source } : {}),
    chainDone: [...(current.chainDone ?? []), bookedLabel],
    patientQuery: saved?.patientName ?? current.patientQuery,
    ...((saved?.patientId ?? current.patientId)
      ? { patientId: saved?.patientId ?? current.patientId }
      : {}),
    ...(nextLeg.profileQuery ? { profileQuery: nextLeg.profileQuery } : {}),
    ...(nextLeg.doctorQuery ? { doctorQuery: nextLeg.doctorQuery } : {}),
    ...(nextLeg.practitionerId ? { practitionerId: nextLeg.practitionerId } : {}),
    ...(nextLeg.profileId ? { profileId: nextLeg.profileId } : {}),
    ...(nextStart ? { start: nextStart } : {}),
    ...(nextLeg.durationMinutes ? { durationMinutes: nextLeg.durationMinutes } : {}),
    ...(rest.length > 0 ? { chain: rest } : {}),
  };
}
